/**
 * ConversationView Component
 * Main messaging interface for fans to chat with creator
 *
 * WIREFRAME COMPONENT - Easy to replace!
 * Customize in config/messagingConfig.ts
 */

import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMessaging } from './MessagingContext'
import { MessageBubble } from './MessageBubble'
import { CREATOR_NAME } from '@/config/brandConfig'
import { ReplyPreview } from './ReplyPreview'
import { GalleryImageReplyPreview } from './GalleryImageReplyPreview'
import { PriorityButton } from './PriorityButton'
import { PriorityPaymentModal } from './PriorityPaymentModal'
import { TipAmountModal } from './TipAmountModal'
import { TipPaymentModal } from './TipPaymentModal'
import { GifButton } from './gif'
import VoiceRecordModal from './components/VoiceRecordModal'
import type { GifAttachment } from './gif'
import { messagingColors, messagingText, messagingBehavior } from '@/config/messagingConfig'
import { getVipProgress } from '@/services/vipService'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000'

export function ConversationView() {
  const navigate = useNavigate()
  const {
    conversation,
    messages,
    isLoading,
    error,
    sendMessage,
    loadMoreMessages,
    hasMoreMessages,
    isTyping,
    adminIsTyping,
    notifyTyping,
    notifyStoppedTyping,
    replyingTo,
    cancelReply,
    replyingToGalleryImage,
    setReplyingToGalleryImage
  } = useMessaging()

  const [messageInput, setMessageInput] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [creatorId, setCreatorId] = useState<string | null>(null)

  // Priority messaging - toggle-based system
  const [priorityModeEnabled, setPriorityModeEnabled] = useState(false)
  const [pendingPriorityMessage, setPendingPriorityMessage] = useState('')
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false)
  const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false)

  // Tip feature state
  const [showTipAmountModal, setShowTipAmountModal] = useState(false)
  const [showTipPaymentModal, setShowTipPaymentModal] = useState(false)
  const [tipAmount, setTipAmount] = useState<number | null>(null)

  // Current user ID for GIF analytics
  const [userId, setUserId] = useState<string>('')

  // VIP status
  const [isVip, setIsVip] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const isUserSendingRef = useRef(false)

  // Scroll position preservation refs (for loading older messages)
  const previousScrollHeightRef = useRef(0)
  const isLoadingOlderRef = useRef(false)
  const lastMessageIdRef = useRef<string | null>(null)

  // Fetch creator ID and user ID on mount
  useEffect(() => {
    async function fetchCreatorId() {
      try {
        const token = localStorage.getItem('auth_token')
        if (!token) return

        const response = await fetch(`${API_URL}/api/messages/creator`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })

        if (response.ok) {
          const data = await response.json()
          setCreatorId(data.data?.id || null)
        }
      } catch (err) {
        console.error('[ConversationView] Failed to fetch creator ID:', err)
      }
    }

    // Get user ID from token for GIF analytics
    function getUserIdFromToken() {
      try {
        const token = localStorage.getItem('auth_token')
        if (token) {
          const payload = JSON.parse(atob(token.split('.')[1]))
          setUserId(payload.sub || payload.user_id || 'anonymous')
        }
      } catch {
        setUserId('anonymous')
      }
    }

    fetchCreatorId()
    getUserIdFromToken()
  }, [])

  // Fetch VIP status on mount
  useEffect(() => {
    getVipProgress()
      .then(data => setIsVip(data.status === 'vip'))
      .catch(() => {})
  }, [])

  // Priority mode is now UI-only state (no server check needed)

  // Preserve scroll position when loading older messages
  useLayoutEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    // Only adjust if we were loading older messages
    if (isLoadingOlderRef.current && previousScrollHeightRef.current > 0) {
      const newScrollHeight = container.scrollHeight
      const heightDifference = newScrollHeight - previousScrollHeightRef.current

      // Adjust scroll to keep user at same position
      if (heightDifference > 0) {
        container.scrollTop = container.scrollTop + heightDifference
      }

      // Reset AFTER adjustment
      previousScrollHeightRef.current = 0
      // Keep isLoadingOlderRef true briefly to block auto-scroll
    }
  }, [messages])

  // Reset loading older flag AFTER the layout effect has run
  useEffect(() => {
    if (isLoadingOlderRef.current) {
      const timer = setTimeout(() => {
        isLoadingOlderRef.current = false
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [messages])

  // Auto-scroll to bottom ONLY for new messages (not older messages)
  // NEVER during user send to prevent iOS keyboard dismissal
  useEffect(() => {
    if (!messagingBehavior.autoScrollOnNewMessage || isUserSendingRef.current) return
    if (messages.length === 0) {
      lastMessageIdRef.current = null
      return
    }

    // Don't auto-scroll if we're loading older messages
    if (isLoadingOlderRef.current) return

    const lastMessage = messages[messages.length - 1]
    const lastMessageId = lastMessage?.id
    const isNewMessage = lastMessageIdRef.current && lastMessageIdRef.current !== lastMessageId

    if (!lastMessageIdRef.current) {
      // First load - scroll to bottom
      const scrollTimer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
      }, 300)
      lastMessageIdRef.current = lastMessageId || null
      return () => clearTimeout(scrollTimer)
    } else if (isNewMessage) {
      // New message arrived - scroll to bottom
      const scrollTimer = setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
      }, 300)
      lastMessageIdRef.current = lastMessageId || null
      return () => clearTimeout(scrollTimer)
    }

    lastMessageIdRef.current = lastMessageId || null
  }, [messages])

  // Handle priority message payment success
  function handlePriorityPaymentSuccess(_paymentIntentId: string) {
    // Reset everything
    setPriorityModeEnabled(false)
    setPendingPriorityMessage('')
    setIsPaymentModalOpen(false)
    setMessageInput('')

    // Show success message
    alert('Priority message sent! Caryn will be notified immediately.')
  }

  // Tip handlers
  function handleTipClick() {
    setShowTipAmountModal(true)
  }

  function handleTipAmountConfirm(amount: number) {
    setTipAmount(amount)
    setShowTipAmountModal(false)
    setShowTipPaymentModal(true)
  }

  function handleTipSuccess() {
    setShowTipPaymentModal(false)
    setTipAmount(null)
    // Message is added via WebSocket, no manual refresh needed
  }

  // Handle GIF selection
  async function handleGifSelect(gif: GifAttachment) {
    try {
      setIsSending(true)
      // Send message with GIF attachment (no text content)
      await sendMessage('', [gif], replyingTo?.id, replyingToGalleryImage?.id)
    } catch (err) {
      console.error('[ConversationView] Failed to send GIF:', err)
    } finally {
      setIsSending(false)
    }
  }

  // Handle send message
  async function handleSend(e: React.FormEvent) {
    e.preventDefault()

    const content = messageInput.trim()
    if (!content || isSending) return

    // MOBILE iOS: Focus BEFORE state changes (during user gesture)
    if (textareaRef.current) {
      textareaRef.current.focus()
    }

    // Clear input
    setMessageInput('')

    // Multiple focus attempts for iOS Safari
    if (textareaRef.current) {
      textareaRef.current.focus()

      requestAnimationFrame(() => {
        if (textareaRef.current) {
          textareaRef.current.focus()
          const len = textareaRef.current.value.length
          textareaRef.current.setSelectionRange(len, len)
        }
      })

      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus()
          const len = textareaRef.current.value.length
          textareaRef.current.setSelectionRange(len, len)
        }
      }, 0)
    }

    try {
      setIsSending(true)

      notifyStoppedTyping()

      // Check if priority mode is enabled
      if (priorityModeEnabled) {
        // Store message to send after payment
        setPendingPriorityMessage(content)
        // Open payment modal
        setIsPaymentModalOpen(true)
        setIsSending(false) // Re-enable UI while payment modal is open
      } else {
        // Regular send (include gallery image reply if present)
        await sendMessage(content, [], replyingTo?.id, replyingToGalleryImage?.id)
        setIsSending(false)
      }
    } catch (err) {
      console.error('Failed to send:', err)
      setIsSending(false)
    }
  }

  // Handle load more (scroll to top)
  function handleScroll() {
    if (!messagesContainerRef.current || !hasMoreMessages) return

    const { scrollTop } = messagesContainerRef.current

    if (scrollTop === 0) {
      // Save scroll height BEFORE loading older messages (for position preservation)
      previousScrollHeightRef.current = messagesContainerRef.current.scrollHeight
      isLoadingOlderRef.current = true
      loadMoreMessages()
    }
  }

  // Show skeleton loading screen (removed old spinner)
  // We'll render the full UI with skeleton bubbles instead

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center max-w-md px-4">
          <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {messagingText.conversation.errorLoading}
          </h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-screen bg-gray-50 animate-fade-in">
      {/* Header */}
      <header className={`${messagingColors.headerBackground} shadow-sm border-b px-4 py-3`}>
        <div className="flex items-center justify-between">
          {/* Back button */}
          <button
            onClick={() => navigate('/welcome')}
            className="p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-smooth"
            aria-label="Back to home"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <div className="flex items-center space-x-3 flex-1 justify-center">
            {/* Creator avatar */}
            {conversation?.creator?.avatar_url ? (
              <img
                src={conversation.creator.avatar_url}
                alt={CREATOR_NAME}
                className="w-10 h-10 rounded-full object-cover"
                onError={(e) => {
                  // Fallback to placeholder if image fails to load
                  e.currentTarget.style.display = 'none'
                  e.currentTarget.nextElementSibling?.classList.remove('hidden')
                }}
              />
            ) : null}
            <div
              className={`w-10 h-10 rounded-full bg-pink-200 flex items-center justify-center text-pink-700 font-semibold ${conversation?.creator?.avatar_url ? 'hidden' : ''}`}
            >
              C
            </div>

            {/* Creator info */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {conversation?.creator?.username || CREATOR_NAME}
              </h2>
              {isTyping && (
                <p className="text-sm text-blue-600 animate-pulse-slow">
                  {messagingText.conversation.typingIndicator}
                </p>
              )}
            </div>
          </div>

          {/* Right side - VIP badge + Verify badge */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {isVip && (
              <span className="text-[10px] font-bold text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                VIP
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Messages container */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 py-6"
      >
        {/* Error message */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg animate-fade-in">
            {error}
          </div>
        )}

        {/* Loading skeleton */}
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex items-end gap-2 max-w-[70%] ${i % 2 === 0 ? 'flex-row-reverse' : 'flex-row'}`}>
                  {/* Avatar placeholder (only for received messages) */}
                  {i % 2 !== 0 && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-200 animate-pulse"></div>
                  )}

                  <div>
                    {/* Message bubble placeholder */}
                    <div className={`px-4 py-3 rounded-2xl ${
                      i % 2 === 0
                        ? 'bg-blue-200 rounded-br-sm'
                        : 'bg-pink-100 rounded-bl-sm'
                    } animate-pulse`}>
                      <div className="h-4 bg-gray-300 rounded w-48 mb-2"></div>
                      <div className="h-4 bg-gray-300 rounded w-32"></div>
                    </div>
                    {/* Timestamp placeholder */}
                    <div className={`mt-1 px-2 h-3 bg-gray-200 rounded w-16 animate-pulse`}></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Load more indicator */}
            {hasMoreMessages && (
              <div className="text-center mb-4">
                <button
                  onClick={loadMoreMessages}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-smooth"
                >
                  Load earlier messages
                </button>
              </div>
            )}

            {/* Messages */}
            {messages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center animate-fade-in">
                  <div className="text-6xl mb-4">👋</div>
                  <p className={`${messagingColors.emptyState} text-lg`}>
                    {messagingText.conversation.emptyState}
                  </p>
                </div>
              </div>
            ) : (
              messages.map((message) => (
                // Key by clientKey when present: it survives the optimistic ->
                // confirmed swap, so the bubble updates in place rather than
                // remounting and replaying its entry animation a second time.
                <MessageBubble key={message.clientKey || message.id} message={message} />
              ))
            )}

            {/* Admin typing indicator — iMessage-style bubble where the next received
                message would appear. Pink bubble with darker pink animated dots. */}
            {adminIsTyping && (
              <div className="flex justify-start mb-4">
                <div className="bg-pink-100 px-4 py-3 rounded-3xl rounded-bl-md inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-pink-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}

            {/* Scroll anchor */}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input area */}
      <div className={priorityModeEnabled
        ? 'bg-gradient-to-r from-amber-50 via-yellow-100 to-amber-50 border-t border-amber-200'
        : `${messagingColors.inputBackground} border-t`}>
        {/* Reply Preview (if replying to message) */}
        {replyingTo && !replyingToGalleryImage && (
          <ReplyPreview
            replyTo={replyingTo}
            onCancel={cancelReply}
          />
        )}

        {/* Gallery Image Reply Preview (if replying to gallery image) */}
        {replyingToGalleryImage && (
          <GalleryImageReplyPreview
            image={replyingToGalleryImage}
            onCancel={() => setReplyingToGalleryImage(null)}
          />
        )}

        <div className="px-4 py-3">
          <form onSubmit={handleSend} className="flex items-center space-x-2">
            {/* GIF button */}
            <GifButton
              onGifSelect={handleGifSelect}
              disabled={isSending}
              userId={userId}
            />

            {/* Priority button - toggle priority mode */}
            <PriorityButton
              priorityEnabled={priorityModeEnabled}
              onToggle={(enabled) => {
                setPriorityModeEnabled(enabled)
              }}
              onTipClick={handleTipClick}
              disabled={isSending}
            />

            {/* Voice note button (record up to 10s, max 3/day) */}
            <button
              type="button"
              onClick={() => setIsVoiceModalOpen(true)}
              disabled={isSending}
              className="flex-shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-pink-600 hover:bg-pink-50 transition-colors disabled:opacity-50"
              aria-label="Record a voice note"
              title="Voice note (up to 10 seconds)"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
              </svg>
            </button>

            {/* Text input */}
            <div className="flex-1">
              <textarea
              ref={textareaRef}
              value={messageInput}
              onChange={(e) => {
                setMessageInput(e.target.value)
                if (e.target.value.length > 0) {
                  notifyTyping()
                } else {
                  notifyStoppedTyping()
                }
              }}
              onBlur={() => notifyStoppedTyping()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()

                  const content = messageInput.trim()
                  if (!content || isSending) return

                  const textarea = e.currentTarget

                  // MOBILE iOS: Must keep focus during user gesture
                  textarea.focus()

                  // Clear input
                  setMessageInput('')

                  // Keep refocusing to maintain keyboard on iOS
                  textarea.focus()

                  requestAnimationFrame(() => {
                    textarea.focus()
                    const len = textarea.value.length
                    textarea.setSelectionRange(len, len)
                  })

                  setTimeout(() => {
                    textarea.focus()
                    const len = textarea.value.length
                    textarea.setSelectionRange(len, len)
                  }, 0)

                  notifyStoppedTyping()

                  // Send in background
                  const doSend = async () => {
                    try {
                      setIsSending(true)
                      if (priorityModeEnabled) {
                        setPendingPriorityMessage(content)
                        setIsPaymentModalOpen(true)
                        setIsSending(false)
                      } else {
                        await sendMessage(content, [], replyingTo?.id, replyingToGalleryImage?.id)
                        setIsSending(false)
                      }
                    } catch (err) {
                      console.error('Send error:', err)
                      setIsSending(false)
                    }
                  }
                  doSend()
                }
              }}
              placeholder={messagingText.conversation.inputPlaceholder}
              rows={1}
              className={priorityModeEnabled
                ? 'w-full px-4 py-3 border border-amber-300 bg-amber-50/30 rounded-2xl focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none'
                : `w-full px-4 py-3 border ${messagingColors.inputBackground} rounded-2xl focus:outline-none focus:ring-2 ${messagingColors.inputFocus} resize-none`}
              style={{ fontSize: '16px' }}
              disabled={isSending}
            />
          </div>

          {/* Send button */}
          <button
            type="submit"
            disabled={!messageInput.trim() || isSending}
            className={priorityModeEnabled
              ? 'p-3 bg-gradient-to-r from-amber-400 to-yellow-400 hover:from-amber-300 hover:to-yellow-300 text-white rounded-full font-semibold btn-scale shadow-lg shadow-amber-500/50 ring-2 ring-amber-400 disabled:opacity-50 disabled:cursor-not-allowed'
              : `p-3 ${messagingColors.sendButton} rounded-full font-semibold btn-scale disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isSending ? (
              <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
              </svg>
            )}
          </button>
        </form>
        </div>
      </div>

      {/* Priority Payment Modal */}
      <VoiceRecordModal
        isOpen={isVoiceModalOpen}
        onClose={() => setIsVoiceModalOpen(false)}
      />

      <PriorityPaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => {
          setIsPaymentModalOpen(false)
          setPendingPriorityMessage('')
        }}
        onSuccess={handlePriorityPaymentSuccess}
        recipientId={conversation?.creator_id || creatorId || ''}
        messageContent={pendingPriorityMessage}
        conversationId={conversation?.id || ''}
      />

      {/* Tip Amount Modal (Apple Cash UI) */}
      <TipAmountModal
        isOpen={showTipAmountModal}
        onClose={() => setShowTipAmountModal(false)}
        onConfirm={handleTipAmountConfirm}
      />

      {/* Tip Payment Modal */}
      <TipPaymentModal
        isOpen={showTipPaymentModal}
        onClose={() => {
          setShowTipPaymentModal(false)
          setTipAmount(null)
        }}
        onSuccess={handleTipSuccess}
        amount={tipAmount || 50}
        recipientId={conversation?.creator_id || creatorId || ''}
        conversationId={conversation?.id || ''}
      />
    </div>
  )
}
